import React, { FunctionComponent, ReactElement } from 'react';
import styled from 'styled-components';
import { useMedia } from 'react-media-match';
import { Widget } from '@getgrover/partners-widget-react';
import useConfig from '@empiriecom/module-components/hooks/useConfig';
import useCookieConsent from '@empiriecom/module-components/hooks/useCookieConsent';
import { renderFont } from '@empiriecom/module-components/ThemeProvider';
import { FragmentConfiguration } from '@/config';
import { AvailabilityState } from '@/src/interfaces';

const GroverWrapper = styled.div<{ mobile: boolean }>`
  .grover-box-container {
    margin: ${({ mobile }) => (mobile ? '1.125rem 0.75rem' : '1.5rem 0 1.188rem')};
    border: ${({ theme }) => `1px solid ${theme.greyNormal.color.normal}`};
    padding: 0.75rem;
    border-radius: 3px;
    overflow: hidden;
    width: auto;
    display: flex;
    background-color: transparent;
    min-width: 0;
  }
  .grover-box-headline-section {
    align-items: baseline;
    align-self: auto;
    width: 100%;
    line-height: 1.125rem;
  }
  .grover-box-headline-text,
  .grover-box-discount-price-text,
  .grover-box-price-text {
    ${({ theme }) => renderFont('base', 'bold', theme)};
    font-size: 0.813rem;
  }
  .grover-box-discount-price-text.grover-discount-price__discount-price {
    color: ${({ theme }) => theme.base.color.text};
  }
  .grover-box-more-info {
    ${({ theme }) => renderFont('base', 'normal', theme)};
    font-size: 0.688rem;
    color: ${({ theme }) => theme.base.color.textTransparent};
  }
  .grover-box-price-container {
    ${({ theme }) => renderFont('base', 'normal', theme)};
    font-size: 0.813rem;
    line-height: 1.125rem;
    margin: 0 0 0.75rem;
  }
  .grover-box-price-container .grover-product-price__starting-price-text {
    margin-right: 3px;
  }
  .grover-box-price-container .grover-product-price__periodicity-text {
    margin-left: 3px;
  }
  .grover-box-original-price-text {
    ${({ theme }) => renderFont('base', 'normal', theme)};
    font-size: 0.813rem;
    text-decoration: line-through;
    margin-right: 3px;
  }
  .grover-box-button {
    background-color: ${({ theme }) => theme.greyLight.color.normal};
    width: 100%;
    height: auto;
    border-radius: 3px;
    overflow: hidden;
    border: ${({ theme }) => `1px solid ${theme.greyNormal.color.normal}`};
    padding: 0.125rem 0;
    margin-bottom: 0.75rem;
  }
  .grover-box-button-text {
    ${({ theme }) => renderFont('base', 'normal', theme)};
    font-size: 0.813rem;
    color: ${({ theme }) => theme.base.color.text};
    order: 1;
  }
  .grover-box-button-icon {
    order: 2;
    margin: 0 0 0 0.75rem;
  }
`;

export const Grover: FunctionComponent<{
  availabilityState: AvailabilityState;
  productId: string;
}> = ({ availabilityState, productId = '' }): ReactElement => {
  const {
    externalServices: {
      grover: { apiKey, consentGroup, moreInformationLink },
    },
  }: FragmentConfiguration = useConfig();

  const isConsentAllowed = useCookieConsent(consentGroup);

  const isMobile = useMedia({ desktop: false, mobile: true, tablet: false });

  return (
    <GroverWrapper mobile={isMobile}>
      {isConsentAllowed && productId !== '' ? (
        <div data-testid="grover-wrapper">
          <Widget
            articleId={productId}
            accessToken={apiKey}
            className="grover-box-container"
            classNames={{
              headerSection: 'grover-box-headline-section',
              headerText: 'grover-box-headline-text',
              moreInfoLink: 'grover-box-more-info',
              priceContainer: 'grover-box-price-container',
              price: 'grover-box-price-text',
              discountPriceContainer: 'grover-box-discount-price-container',
              discountPrice: 'grover-box-discount-price-text',
              originalPrice: 'grover-box-original-price-text',
              button: 'grover-box-button',
              buttonIcon: 'grover-box-button-icon',
              buttonText: 'grover-box-button-text',
            }}
            moreInformationUrl={moreInformationLink}
            stockEnumerated={availabilityState !== 'NOT_AVAILABLE' ? 'medium' : 'none'}
          />
        </div>
      ) : null}
    </GroverWrapper>
  );
};

export default Grover;
